import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import * as api from 'lib/api';
import Modal from 'components/Modal';
import Button from 'components/Button';
import colors from 'config/colors';
import { MdLockOutline } from 'react-icons/md';
import { initialSubmission, localTime, submissionError, submissionPayload } from './admin-submission-form';
import './admin-timesheet-submission.scss';

export function AdminSubmissionForm({ context, timesheetKey, shiftReference, onClose, onSubmitted }) {
  const [draft, setDraft] = useState(() => initialSubmission(context));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const nextId = useRef(1);
  const change = (key, value) => setDraft(previous => ({ ...previous, [key]: value }));
  const updateRow = (collection, id, key, value) => setDraft(previous => ({ ...previous, [collection]: previous[collection].map(row => (row.id === id ? { ...row, [key]: value } : row)) }));
  const removeRow = (collection, id) => setDraft(previous => ({ ...previous, [collection]: previous[collection].filter(row => row.id !== id) }));
  const addRow = collection => {
    const id = nextId.current;
    nextId.current += 1;
    change(collection, [...draft[collection], { id, timeTypeId: '', start: draft.start, end: draft.end }]);
  };
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || complete) return;
    setError('');
    let payload;
    try { payload = submissionPayload(draft, context, timesheetKey); } catch (err) { setError(err.message); return; }
    inFlight.current = true;
    setSubmitting(true);
    try {
      const response = await api.post('timesheets/submit-admin', payload);
      if (response.status !== 200 || !response.body?.success) { setError(submissionError(response)); return; }
      setComplete(true);
      await onSubmitted();
      onClose();
    } catch (err) {
      if (mounted.current) setError('The result could not be refreshed. Close this window and refresh the timesheet before trying again.');
    } finally {
      if (mounted.current) setSubmitting(false);
      inFlight.current = false;
    }
  };
  const dateField = (label, value, onChange, id) => (
    <label htmlFor={id}>
      {label}
      <input id={id} type="datetime-local" required value={value} onChange={event => onChange(event.target.value)} step="60" />
    </label>
  );
  const renderRange = (row, collection, index) => (
    <div className="admin-timesheet-form__block" key={row.id}>
      <div className="admin-timesheet-form__row">
        {dateField('Start', row.start, value => updateRow(collection, row.id, 'start', value), `${collection}-${row.id}-start`)}
        {dateField('End', row.end, value => updateRow(collection, row.id, 'end', value), `${collection}-${row.id}-end`)}
      </div>
      {collection === 'rows' && (
        <label htmlFor={`type-${row.id}`}>Time type
          <select id={`type-${row.id}`} required value={row.timeTypeId} onChange={event => updateRow(collection, row.id, 'timeTypeId', event.target.value)}>
            <option value="">Select a time type</option>
            {context.timeTypes.map(type => <option value={type.key} key={type.key}>{type.name}</option>)}
          </select>
        </label>
      )}
      <Button small outline onClick={() => removeRow(collection, row.id)}>Remove {collection === 'rows' ? 'time block' : 'break'} {index + 1}</Button>
    </div>
  );
  const policy = context.policies;
  const scheduled = `${moment.utc(context.shiftStartTime).tz(context.timezone).format('DD MMM YYYY HH:mm')} – ${moment.utc(context.shiftEndTime).tz(context.timezone).format('DD MMM YYYY HH:mm')}`;
  return (
    <Modal isOpen header="Submit Timesheet" contentLabel="Submit Timesheet" onRequestClose={() => { if (!inFlight.current) onClose(); }} contentStyle={{ width: 660 }} bodyStyle={{ padding: 24, overflow: 'auto' }}>
      <form className="admin-timesheet-form" onSubmit={submit}>
        <p>Complete the submission for <strong>{context.candidateName}</strong>{shiftReference ? ` · ${shiftReference}` : ''}</p>
        <p>Scheduled: {scheduled}</p>
        <p className="admin-timesheet-form__notice">You are helping the candidate complete their timesheet submission. The audit trail will record you as the person who submitted it. The timesheet will then be sent for approval.</p>
        {(policy.earlyShiftStart !== undefined || policy.lateShiftFinish !== undefined) && <p className="admin-timesheet-form__hint">{policy.earlyShiftStart !== undefined ? `Up to ${policy.earlyShiftStart} minutes before the scheduled start. ` : ''}{policy.lateShiftFinish !== undefined ? `Up to ${policy.lateShiftFinish} minutes after the scheduled finish.` : ''}</p>}
        <fieldset disabled={submitting || complete}>
          {context.timesheetTypeKey === 'start-end' && (
            <div className="admin-timesheet-form__row">
              {dateField('Start', draft.start, value => change('start', value), 'timesheet-start')}
              {dateField('End', draft.end, value => change('end', value), 'timesheet-end')}
            </div>
          )}
          {context.timesheetTypeKey === 'on-site-off-site' && (
            <div className="admin-timesheet-form__row">
              <label htmlFor="on-site">On-site minutes<input id="on-site" type="number" min="0" step="1" required value={draft.onSite} onChange={event => change('onSite', event.target.value)} /></label>
              <label htmlFor="off-site">Off-site minutes<input id="off-site" type="number" min="0" step="1" required value={draft.offSite} onChange={event => change('offSite', event.target.value)} /></label>
            </div>
          )}
          {context.timesheetTypeKey === 'start-end-multi' && (
            <section aria-label="Time blocks">
              <h3>Time blocks</h3>
              {draft.rows.map((row, index) => renderRange(row, 'rows', index))}
              <Button outline onClick={() => addRow('rows')}>Add time block</Button>
              {!context.timeTypes.length && <p role="alert">No time types are configured for this shift.</p>}
            </section>
          )}
          {context.timesheetTypeKey !== 'on-site-off-site' && (
            <section aria-label="Breaks">
              <h3>Breaks</h3>
              {draft.breaks.map((row, index) => renderRange(row, 'breaks', index))}
              <Button
                outline
                onClick={() => {
                  const id = nextId.current;
                  nextId.current += 1;
                  change('breaks', [...draft.breaks, { id, start: draft.start, end: localTime(moment.tz(draft.start, context.timezone).add(15, 'minutes').toISOString(), context.timezone) }]);
                }}
              >Add break
              </Button>
            </section>
          )}
          {context.requiresWitness && (
            <div className="admin-timesheet-form__row">
              <label htmlFor="witness-name">Witness name<input id="witness-name" required value={draft.witnessName} onChange={event => change('witnessName', event.target.value)} /></label>
              <label htmlFor="witness-role">Witness role<input id="witness-role" required value={draft.witnessRole} onChange={event => change('witnessRole', event.target.value)} /></label>
            </div>
          )}
        </fieldset>
        {error && <p role="alert" className="admin-timesheet-form__error">{error}</p>}
        {complete && <p role="status">Timesheet submitted for approval.</p>}
        <div className="admin-timesheet-form__actions">
          <Button outline disabled={submitting} onClick={onClose}>Cancel</Button>
          <Button style={{ backgroundColor: colors.dagny.primary, color: colors.text }} submit disabled={submitting || complete || (context.timesheetTypeKey === 'start-end-multi' && !context.timeTypes.length)}>{submitting ? 'Submitting…' : 'Submit for approval'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminTimesheetSubmission({ timesheetKey, shiftReference, onSubmitted, onExistingSubmission }) {
  const [context, setContext] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retryable, setRetryable] = useState(false);
  const [submissionExists, setSubmissionExists] = useState(false);
  useEffect(() => {
    let active = true;
    setContext(null);
    setError('');
    setRetryable(false);
    setSubmissionExists(false);
    setLoading(true);
    api.get('timesheets/admin-submission-context', { timesheetKey }).then(response => {
      if (!active) return;
      setLoading(false);
      if (response.status === 200 && response.body?.available === false) return;
      if (response.status === 200 && response.body?.context?.timesheetTypeKey) {
        setContext(response.body.context);
        return;
      }
      const reason = response.body?.error?.message || response.body?.message || (typeof response.body?.error === 'string' && response.body.error);
      if (response.status === 409 && typeof reason === 'string' && reason.startsWith('This timesheet already has a submission')) {
        setSubmissionExists(true);
        onExistingSubmission?.();
        return;
      }
      const canRetry = ![403, 409, 422].includes(response.status);
      setRetryable(canRetry);
      if (response.status === 404 && !reason) {
        setError('Timesheet submission is not available in this environment yet. Please contact your administrator.');
      } else if ([403, 404, 409, 422].includes(response.status) && reason) {
        const messages = {
          'Admin is not in the correct admin group for this timesheet': 'You do not belong to an admin group that can manage this timesheet.',
          'Admin does not have permission to submit locked timesheets': 'You do not have permission to submit locked timesheets.',
          'Admin cannot manage this timesheet': 'You do not have permission to manage this timesheet.',
        };
        setError(messages[reason] || reason);
      } else {
        setError(canRetry ? 'Unable to load timesheet submission details. Please retry.' : 'Submission is not available for this timesheet.');
      }
    }).catch(() => {
      if (active) {
        setLoading(false);
        setRetryable(true);
        setError('Unable to load timesheet submission details. Please retry.');
      }
    });
    return () => { active = false; };
  }, [timesheetKey, attempt, onExistingSubmission]);
  return (
    <div className="timesheet-submission__footer">
      {error && <p className="timesheet-submission__warning" role="alert">{error} {retryable && <Button small onClick={() => setAttempt(value => value + 1)}>Retry</Button>}</p>}
      {submissionExists && <p role="status">Submission recorded. See Messages &amp; Audit Trail for details.</p>}
      {loading && <p className="admin-timesheet-action__hint" role="status">Checking submission availability…</p>}
      {context && (
        <div className="admin-timesheet-action">
          <p className="admin-timesheet-action__notice"><MdLockOutline size={22} aria-hidden="true" />This timesheet is locked. You can help the candidate complete their submission.</p>
          <Button style={{ backgroundColor: colors.dagny.primary, color: colors.text }} large onClick={() => setOpen(true)}>Submit Timesheet</Button>
          <p className="admin-timesheet-action__hint">Review the worked times before sending for approval.</p>
        </div>
      )}
      {open && context && <AdminSubmissionForm context={context} timesheetKey={timesheetKey} shiftReference={shiftReference} onClose={() => setOpen(false)} onSubmitted={async () => { await onSubmitted(); setContext(null); }} />}
    </div>
  );
}
